// Memoria delle risposte condivisa da tutte le pagine, finché la scheda del browser resta aperta.
// Tornando su una pagina i dati già scaricati compaiono subito; se sono più vecchi di maxAge si
// mostrano lo stesso e intanto si aggiornano. Due pagine che chiedono la stessa cosa (stessa
// chiave) condividono la stessa richiesta, anche se una è ancora in corso.

export type Loader<T> = (signal: AbortSignal) => Promise<T>;

export interface Query<T> {
  // Deve cambiare con tutto ciò che cambia la risposta (filtri, date, pagina…)
  key: string;
  load: Loader<T>;
  maxAge?: number;
}

export const DEFAULT_MAX_AGE = 5 * 60_000;
// Elenchi che cambiano di rado (materie, scuole, metriche disponibili)
export const REFERENCE_MAX_AGE = 30 * 60_000;
const MAX_ENTRIES = 50;

interface Entry {
  hasData: boolean;
  data?: unknown;
  time: number;
  promise?: Promise<unknown>;
  controller?: AbortController;
  // Componenti che stanno usando questa chiave in questo momento
  users: number;
}

const entries = new Map<string, Entry>();
const clearListeners = new Set<() => void>();

const entryFor = (key: string): Entry => {
  let entry = entries.get(key);
  if (!entry) {
    entry = { hasData: false, time: 0, users: 0 };
    entries.set(key, entry);
  }
  return entry;
};

// Le chiavi meno recenti senza utenti né richieste in corso escono per prime
const prune = () => {
  for (const [key, entry] of Array.from(entries)) {
    if (entries.size <= MAX_ENTRIES) return;
    if (entry.users <= 0 && !entry.promise) entries.delete(key);
  }
};

export const peekCached = <T>(key: string): { data: T } | null => {
  const entry = entries.get(key);
  return entry?.hasData ? { data: entry.data as T } : null;
};

export const isFresh = (key: string, maxAge = DEFAULT_MAX_AGE) => {
  const entry = entries.get(key);
  return Boolean(entry?.hasData && Date.now() - entry.time < maxAge);
};

// Scarica (o si aggancia alla richiesta già in corso per la stessa chiave) e salva la risposta
export const fetchCached = <T>(key: string, load: Loader<T>): Promise<T> => {
  const entry = entryFor(key);
  if (entry.promise) return entry.promise as Promise<T>;
  const controller = new AbortController();
  const promise: Promise<T> = load(controller.signal).then(
    (data) => {
      if (entry.promise === promise) {
        entry.promise = undefined;
        entry.controller = undefined;
        entry.hasData = true;
        entry.data = data;
        entry.time = Date.now();
        // In fondo alla coda: è la più recente
        entries.delete(key);
        entries.set(key, entry);
        prune();
      }
      return data;
    },
    (error) => {
      if (entry.promise === promise) {
        entry.promise = undefined;
        entry.controller = undefined;
      }
      throw error;
    },
  );
  entry.promise = promise;
  entry.controller = controller;
  return promise;
};

export const holdCached = (key: string) => {
  entryFor(key).users += 1;
};

// abort: annulla la richiesta in corso se nessun altro la sta aspettando. Si usa quando un filtro
// cambia; uscendo dalla pagina invece la richiesta finisce e la risposta resta pronta per il ritorno.
export const releaseCached = (key: string, abort: boolean) => {
  const entry = entries.get(key);
  if (!entry) return;
  entry.users = Math.max(0, entry.users - 1);
  if (abort && entry.users === 0 && entry.promise) {
    entry.controller?.abort();
    entry.promise = undefined;
    entry.controller = undefined;
  }
  if (entry.users === 0 && !entry.promise && !entry.hasData) entries.delete(key);
};

// Altre memorie a livello di modulo (es. l'elenco famiglie) si svuotano insieme a questa
export const onCacheClear = (listener: () => void) => {
  clearListeners.add(listener);
  return () => {
    clearListeners.delete(listener);
  };
};

// All'uscita dall'account: i dati di chi è uscito non devono restare in memoria
export const clearQueryCache = () => {
  entries.forEach((entry) => entry.controller?.abort());
  entries.clear();
  clearListeners.forEach((listener) => listener());
};
